import Select from "antd/es/select";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Icon, Input, Pagination } from "semantic-ui-react";
import ERModal from "../../components/ERModal";
import LeaveManagementTable from "../../components/LeaveManagementTable";
import { exportFileAction } from "../../core/actions/commonAction";
import {
    clearMessageLeaveOverview,
    getLeaveOverview,
    updateLeaveOverview,
} from "../../core/actions/leaveOverviewAction";
import { getDataDepartmentAndEmployee } from "../../core/actions/totalRecordByDateAction";
import functionCommon from "../../core/common/function";
import { commonConstants } from "../../core/constant/common.constants";
import "./styles.scss";

const initParams = {
    leaveOverview: {
        isSearch: 0,
        departmentId: "",
        employeeId: "",
        sortBy: "firstName-ASC",
        pageRecord: 20,
    },
    department: {
        departmentId: "",
        departmentBy: "createdDate-ASC",
        employeeBy: "firstName-ASC",
    },
};

const isHR = functionCommon.getRole() === "hr";

export default function LeaveOverview() {
    const { t } = useTranslation();
    const [isDisableUpdate, setIsDisableUpdate] = useState(true);
    const [isDisableTextBox, setIsDisableTextBox] = useState(true);
    const [activeItem, setActiveItem] = useState(null);
    const dispatch = useDispatch();
    const department = useSelector((state) => state.departmentAndEmployeeReducer);
    const { getting, isLoading } = department;
    const leaveOverviewStore = useSelector((state) => state.leaveOverviewReducer);
    const { searching, updating, successCode, errorCode, isLoadingPage } = leaveOverviewStore;
    const [conditions, setConditions] = useState(initParams.leaveOverview);
    const [dataUpdate, setDataUpdate] = useState({
        otherAddition: "",
        otherDeduction: "",
    });
    const [messageTable, setMessageTable] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const conditionRef = useRef(initParams.leaveOverview);
    const [openModal, setOpenModal] = useState(false);

    const handleActiveItem = (index) => {
        setActiveItem(index);
        const item = leaveOverviewStore.listOverview.getLeaveOverview.find((ele) => ele.employeeId === index);
        if (item) {
            const departmentItem =
                item.employeeDepartment.length > 1
                    ? item.employeeDepartment.find((ele) => Number(ele.isMain) === 1)
                    : item.employeeDepartment[0];
            setConditions({
                ...conditions,
                departmentId: departmentItem ? Number(departmentItem.departmentId) : "",
            });
        }
    };

    useEffect(() => {
        if (!leaveOverviewStore.listOverview.getLeaveOverview) return;
        const item = leaveOverviewStore.listOverview.getLeaveOverview.find((ele) => ele.employeeId === activeItem);
        if (item) {
            const departmentItem =
                item.employeeDepartment.length > 1
                    ? item.employeeDepartment.find((ele) => Number(ele.isMain) === 1)
                    : item.employeeDepartment[0];
            dispatch(
                getDataDepartmentAndEmployee({ ...initParams.department, departmentId: departmentItem.departmentId })
            );
        }
    }, [conditions.departmentId]);

    const onChangeModal = (value) => () => {
        setOpenModal(value);
    };

    const onKeyDown = (event) => {
        functionCommon.onKeyDownNumber(event, true);
    };

    const handleChangeForm = (label) => (value) => {
        if (label === "departmentId") {
            dispatch(getDataDepartmentAndEmployee({ ...initParams.department, departmentId: value }));
            setActiveItem(null);
            setConditions({
                ...conditions,
                [label]: value,
                employeeId: "",
            });
        } else {
            setConditions({
                ...conditions,
                [label]: value,
            });
        }
    };

    const handlePaginationChange = (e, { activePage }) => {
        setCurrentPage(activePage);
        dispatch(getLeaveOverview({ ...conditionRef.current, currentPage: activePage }, false, true));
    };

    const handleChangeData = (label) => (event) => {
        let newValue = functionCommon.onInputNumberPositive(event, true);
        const isValidDigit =
            String(newValue).indexOf(".") < 0 ||
            (String(newValue).indexOf(".") >= 0 && String(newValue).split(".")[1].length <= 1);
        if (Number(newValue) <= commonConstants.NUMBER_DAY_IN_YEAR && isValidDigit) {
            setDataUpdate({
                ...dataUpdate,
                [label]: newValue,
            });
        }
    };

    const onBlur = (label) => (event) => {
        const value = parseFloat(event.target.value);
        setDataUpdate({
            ...dataUpdate,
            [label]: isNaN(value) || value === 0 ? "" : String(value),
        });
    };

    const handleSearch = () => {
        dispatch(getLeaveOverview({ ...conditions, isSearch: 1, currentPage: 1 }, true));
        conditionRef.current = { ...conditions, isSearch: 1 };
        setCurrentPage(1);
        setActiveItem(null);
    };

    const handleClearAll = () => {
        setConditions({ ...conditions, departmentId: "", employeeId: "" });
        setDataUpdate({ ...dataUpdate, otherAddition: "", otherDeduction: "" });
        setActiveItem(null);
    };

    const handleUpdate = () => {
        dispatch(
            updateLeaveOverview(
                {
                    employeeId: activeItem,
                    otherAddition: dataUpdate.otherAddition,
                    otherDeduction: dataUpdate.otherDeduction,
                },
                conditionRef.current
            )
        );
        setConditions({ ...conditions, departmentId: "", employeeId: "" });
        setDataUpdate({ ...dataUpdate, otherAddition: "", otherDeduction: "" });
        setActiveItem(null);
    };

    const departmentProcess = (rawData) => {
        if (rawData && rawData.length > 0) {
            const department = [];
            rawData.forEach((ele) => {
                department.push({
                    value: ele.departmentId,
                    name: ele.departmentName,
                });
            });
            return department;
        } else return [];
    };

    const employeeProcess = (rawData) => {
        if (rawData && rawData.length > 0) {
            const employee = [];
            rawData.forEach((ele) => {
                employee.push({
                    value: ele.employeeId,
                    name: ele.employeeCode + " - " + ele.employeeName,
                });
            });
            return employee;
        } else return [];
    };

    const processLeaveType = (rawData) => {
        const result = [0, 0, 0, 0, 0, 0];
        if (rawData && rawData.length > 0) {
            rawData.forEach((ele) => {
                if (Number(ele.leaveTypeId) >= 1 && Number(ele.leaveTypeId) <= 6) {
                    result[Number(ele.leaveTypeId) - 1] = Number(ele.availableTime);
                }
            });
        }
        return result;
    };

    const handleExport = (value) => {
        let params = {};
        if (value.year && !value.status) {
            params = {
                "old-year": value.year,
            };
        } else if (!value.year && value.status) {
            params = {
                status: value.status,
            };
        } else if (value.year && value.status) {
            params = {
                status: value.status,
                "old-year": value.year,
            };
        }
        dispatch(
            exportFileAction(
                {
                    urlGetkey: "Leave/requestSecretKey",
                    urlDownfile: "Leave/exportLeaveOverview",
                },
                { secretKey: "", ...params }
            )
        );
    };

    const triggerMessage = () => {
        setTimeout(() => {
            setMessageTable(null);
            dispatch(clearMessageLeaveOverview());
        }, 2000);
    };

    const dataTable = useMemo(() => {
        const tableContent = {
            header: [
                t("labels.id"),
                t("labels.employeeCode"),
                t("labels.employeeName"),
                t("labels.department"),
                <div className="noted-header">
                    <p>Annual Leave</p>
                    <p className="note">(1)</p>
                </div>,
                <div className="noted-header">
                    <p>Hazardous Work</p>
                    <p className="note">(2)</p>
                </div>,
                <div className="noted-header">
                    <p>Seniority</p>
                    <p className="note">(3)</p>
                </div>,
                <div className="noted-header">
                    <p>Carried Over</p>
                    <p className="note">(4)</p>
                </div>,
                <div className="noted-header">
                    <p>Other Addition</p>
                    <p className="note">(5)</p>
                </div>,
                <div className="noted-header">
                    <p>{t("labels.totalCurrentHave")}</p>
                    <p className="note">(6) = 1 + 2 + 3 + 4 + 5</p>
                </div>,
                <div className="noted-header">
                    <p>{t("labels.totalLeave")}</p>
                    <p className="note">(7)</p>
                </div>,
                <div className="noted-header">
                    <p>Other Deduction</p>
                    <p className="note">(8)</p>
                </div>,
                <div className="noted-header">
                    <p>{t("labels.totalInYear")}</p>
                    <p className="note">(9) = 6 + 7</p>
                </div>,
                t("labels.confirmation"),
            ],
        };
        if (!isLoading) return tableContent;
        if (
            leaveOverviewStore.listOverview.getLeaveOverview &&
            leaveOverviewStore.listOverview.getLeaveOverview.length > 0
        ) {
            const body = [];
            const arrayValue = [];
            leaveOverviewStore.listOverview.getLeaveOverview.forEach((ele, idx) => {
                const item = [];
                const listLeaveType = processLeaveType(ele.leaveAvailable);
                let totalHave = 0;
                item.push(
                    (Number(currentPage) - 1) * Number(leaveOverviewStore.listOverview.totalPageRecord) + idx + 1
                );
                item.push(ele.employeeCode);
                item.push(ele.employeeName);
                item.push(ele.employeeDepartment.map((ele) => ele.departmentName).join(", "));
                for (let i = 0; i < 5; i++) {
                    item.push(listLeaveType[i]);
                    totalHave = totalHave + Number(listLeaveType[i]) * 100;
                }
                item.push(parseInt(totalHave) / 100);
                item.push(Number(ele.totalLeave));
                item.push(listLeaveType[5]);
                item.push((parseInt(Number(item[9]) * 100) + parseInt(Number(item[10]) * 100)) / 100);
                item.push(Number(ele.is_carriedOver) ? t("labels.yes") : t("labels.no"));
                body.push(item);
                arrayValue.push(ele.employeeId);
            });
            tableContent.body = body;
            tableContent.arrayValue = arrayValue;
            setMessageTable(null);
        } else {
            setMessageTable({ text: "message.018", isError: true });
            triggerMessage();
        }
        return tableContent;
    }, [leaveOverviewStore.listOverview, isLoading, t]);

    useEffect(() => {
        if (leaveOverviewStore.listOverview.getLeaveOverview && activeItem) {
            const item = leaveOverviewStore.listOverview.getLeaveOverview.find((ele) => ele.employeeId === activeItem);
            setConditions({
                ...conditions,
                employeeId: item ? Number(item.employeeId) : "",
            });
        }
    }, [activeItem]);

    useEffect(() => {
        if (dataUpdate.otherAddition != "" || dataUpdate.otherDeduction != "") setIsDisableUpdate(false);
        else setIsDisableUpdate(true);
    }, [dataUpdate]);

    useEffect(() => {
        setIsDisableTextBox(activeItem === null);
    }, [activeItem]);

    useEffect(() => {
        dispatch(getLeaveOverview({ ...initParams.leaveOverview, currentPage: currentPage }));
        dispatch(getDataDepartmentAndEmployee(initParams.department));
    }, []);

    useEffect(() => {
        if (successCode) {
            setMessageTable({ text: `message.${successCode}`, isError: false });
            triggerMessage();
        }
    }, [successCode]);

    useEffect(() => {
        if (errorCode) {
            setMessageTable({ text: `message.${errorCode}`, isError: true });
            triggerMessage();
        }
    }, [errorCode]);

    return (
        <div className="leave-overview">
            <h2 className="page-heading">
                <span onClick={functionCommon.goToPage("/my-leave-request")}>{t("labels.leaveManagemnet")}</span>
                {"/ "}
                <span onClick={functionCommon.goToPage("/leave-overview")}>{t("labels.leaveOverview")}</span>
            </h2>
            <div className="form">
                <div className="button-group">
                    <Button className="leave-button" onClick={handleSearch} disabled={searching}>
                        <Icon name="search" />
                        {searching ? t("button.searching") : t("button.search")}
                    </Button>
                    {isHR && (
                        <Button className="leave-button" onClick={handleUpdate} disabled={isDisableUpdate || updating}>
                            <Icon name="edit" />
                            {updating ? t("button.updating") : t("button.update")}
                        </Button>
                    )}
                    <Button className="leave-button" onClick={onChangeModal(true)}>
                        <Icon name="file excel outline" />
                        {t("button.export")}
                    </Button>
                    <Button onClick={handleClearAll}>
                        <Icon name="refresh" />
                        {t("button.clearAll")}
                    </Button>
                </div>
                <div className={`controller ${isHR ? "" : "left"}`}>
                    <div className="item">
                        <label>{t("labels.department")}</label>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            value={conditions.departmentId}
                            onChange={handleChangeForm("departmentId")}
                        >
                            <Select.Option value="" label="All">
                                All
                            </Select.Option>
                            {department.listDepartmentSelectBox.length > 0 &&
                                departmentProcess(department.listDepartmentSelectBox).map((ele, idx) => {
                                    return (
                                        <Select.Option key={idx} value={ele.value} label={ele.name}>
                                            {ele.name}
                                        </Select.Option>
                                    );
                                })}
                        </Select>
                    </div>
                    <div className="item">
                        <label>{t("navigation.employee")}</label>
                        <Select
                            showSearch
                            filterOption={functionCommon.filterEmployee}
                            value={getting ? null : conditions.employeeId}
                            onChange={handleChangeForm("employeeId")}
                        >
                            <Select.Option value="" label="All">
                                All
                            </Select.Option>
                            {department.listEmployeeWorkingSelectBox.length > 0 &&
                                employeeProcess(department.listEmployeeWorkingSelectBox).map((ele, idx) => {
                                    return (
                                        <Select.Option key={idx} value={ele.value} label={ele.name}>
                                            {ele.name}
                                        </Select.Option>
                                    );
                                })}
                        </Select>
                    </div>
                    {isHR && (
                        <>
                            <div className="item">
                                <label>Other Addition</label>
                                <Input
                                    min="0"
                                    type="text"
                                    value={dataUpdate.otherAddition}
                                    onKeyDown={onKeyDown}
                                    onPaste={functionCommon.onPasteNumber}
                                    disabled={isDisableTextBox}
                                    onBlur={onBlur("otherAddition")}
                                    onChange={handleChangeData("otherAddition")}
                                />
                            </div>
                            <div className="item">
                                <label>Other Deduction</label>
                                <Input
                                    min="0"
                                    type="text"
                                    value={dataUpdate.otherDeduction}
                                    onKeyDown={onKeyDown}
                                    onPaste={functionCommon.onPasteNumber}
                                    disabled={isDisableTextBox}
                                    onBlur={onBlur("otherDeduction")}
                                    onChange={handleChangeData("otherDeduction")}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="table">
                {messageTable && (
                    <div className={`${messageTable.isError ? "error-message" : "success-message"} list-error-message`}>
                        {t(messageTable.text)}
                    </div>
                )}
                <LeaveManagementTable
                    content={dataTable}
                    onActiveItem={handleActiveItem}
                    activeItem={activeItem}
                    customTableClass="table-custom"
                    isClickItem
                    isUnStackable={true}
                />
                {leaveOverviewStore.listOverview.totalPage && (
                    <div className="leave-table-paging">
                        <Pagination
                            size="tiny"
                            activePage={currentPage}
                            firstItem={null}
                            lastItem={null}
                            disabled={isLoadingPage}
                            totalPages={leaveOverviewStore.listOverview.totalPage}
                            prevItem={{
                                disabled: currentPage === 1 || isLoadingPage,
                                content: <Icon name="chevron left" />,
                                icon: true,
                            }}
                            nextItem={{
                                disabled: currentPage === leaveOverviewStore.listOverview.totalPage || isLoadingPage,
                                content: <Icon name="chevron right" />,
                                icon: true,
                            }}
                            onPageChange={handlePaginationChange}
                        />
                    </div>
                )}
            </div>
            {openModal && (
                <ERModal
                    size="tiny"
                    open={openModal}
                    modalStyle="overViewModal"
                    handleClose={onChangeModal(false)}
                    handleExportOverview={handleExport}
                />
            )}
        </div>
    );
}
